import Axios from 'axios';
import VueCookies from 'vue-cookies';
import { createToast } from 'mosha-vue-toastify';
import AnalyzerStateManager from '../services/AnalyzerStateManager';

/**
 * Appels analyzer via le backend (proxy). Le front n'appelle plus l'analyzer directement.
 * baseURL = backend ; les chemins sont préfixés pour atteindre /api/v1/analyzer/* sans doublon.
 * L'auth est gérée par le backend (cookie/session) via l'adapter principal.
 */
let APIBaseURL = process.env.VUE_APP_BACKEND_URL || '';
// En dev uniquement (localhost) : forcer /api/v1 pour le proxy. En prod, garder VUE_APP_BACKEND_URL.
const isLocalhost = typeof window !== 'undefined' && /^localhost$|^127\.0\.0\.1$/.test(window.location.hostname);
if (isLocalhost && APIBaseURL.startsWith('http') && !APIBaseURL.startsWith(window.location.origin)) {
  APIBaseURL = '/api/v1';
}
// Éviter /api/v1/api/v1/analyzer quand VUE_APP_BACKEND_URL se termine déjà par /api/v1
const ANALYZER_PREFIX = APIBaseURL.replace(/\/+$/, '').endsWith('/api/v1') ? '/analyzer' : '/api/v1/analyzer';

function analyzerPath(path) {
  const p = path.startsWith('/analyzer') ? path.slice(9) : path;
  return `${ANALYZER_PREFIX}${p.startsWith('/') ? p : '/' + p}`;
}

function normalizeInstagramIdentifier(input) {
  const raw = String(input || '').trim();
  if (!raw) return '';

  if (!raw.includes('instagram.com')) {
    return raw.replace(/^@+/, '').trim();
  }

  try {
    const url = new URL(raw.startsWith('http') ? raw : `https://${raw}`);
    const segments = url.pathname.split('/').filter(Boolean);
    return String(segments[0] || '').replace(/^@+/, '').trim();
  } catch (_) {
    const withoutProtocol = raw.replace(/^https?:\/\//i, '');
    const afterHost = withoutProtocol.split('/').slice(1).join('/');
    const firstSegment = (afterHost.split('/').filter(Boolean)[0] || '')
      .split('?')[0]
      .split('#')[0];
    return String(firstSegment || '').replace(/^@+/, '').trim();
  }
}

function normalizeFacebookTargetAccount(input) {
  const raw = String(input || '').trim();
  if (!raw) return '';

  if (!raw.toLowerCase().includes('facebook.com')) {
    const handle = raw
      .replace(/^@+/, '')
      .replace(/^\/+/, '')
      .replace(/\/+$/, '')
      .trim();
    if (!handle) return '';
    return `https://www.facebook.com/${handle}/`;
  }

  try {
    const parsed = new URL(raw.startsWith('http') ? raw : `https://${raw}`);
    const segments = parsed.pathname.split('/').filter(Boolean);
    const first = String(segments[0] || '').toLowerCase();

    if (first === 'profile.php') {
      const id = parsed.searchParams.get('id');
      return id
        ? `https://www.facebook.com/profile.php?id=${encodeURIComponent(id)}`
        : 'https://www.facebook.com/profile.php';
    }

    if (segments.length === 0) return 'https://www.facebook.com/';
    return `https://www.facebook.com/${segments.join('/')}/`;
  } catch (_) {
    const withoutProtocol = raw.replace(/^https?:\/\//i, '');
    const afterHost = withoutProtocol.split('/').slice(1).join('/');
    const firstSegment = (afterHost.split('/').filter(Boolean)[0] || '')
      .split('?')[0]
      .split('#')[0]
      .replace(/^@+/, '')
      .trim();
    if (!firstSegment) return '';
    return `https://www.facebook.com/${firstSegment}/`;
  }
}

if (!APIBaseURL) {
  createToast(`VUE_APP_BACKEND_URL n'est pas configuré. Veuillez redémarrer le serveur de développement.`, {
    position: 'bottom-center',
    hideProgressBar: true,
    timeout: 8000,
    transition: 'slide',
    toastBackgroundColor: '#ff4545',
  });
}

const adapter = Axios.create({
  baseURL: APIBaseURL || '',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 secondes timeout par défaut
  withCredentials: true,
});

let authRecoveryInProgress = false;

// Les cookies HttpOnly sont envoyés automatiquement via withCredentials: true

// Interceptor pour la gestion des erreurs
adapter.interceptors.response.use(
  (response) => {
    eachRecursive(response.data);
    return response;
  },
  (error) => {
    const suppressGlobalErrorToast =
      error?.config?.suppressGlobalErrorToast === true;

    if (error.response) {
      // Les appels analyzer passent par le backend ; 401/403 = session invalide
      if (error.response.status === 401 || error.response.status === 403) {
        if (!authRecoveryInProgress) {
          authRecoveryInProgress = true;
          import('../store')
            .then(async ({ default: store }) => {
              try {
                const session = await store.dispatch('initSession', {
                  forceNetwork: true,
                  clearOnUnauthenticated: true,
                });
                if (!session || session.isTmp) {
                  VueCookies.remove('adminToken');
                  VueCookies.remove('adminProfile');
                  window.location.href = '/signin';
                  return;
                }
              } catch (_e) {
                window.location.href = '/signin';
                return;
              } finally {
                authRecoveryInProgress = false;
              }
            })
            .catch(() => {
              authRecoveryInProgress = false;
              window.location.href = '/signin';
            });
        }
      }

      if (error.response.status === 404 && !suppressGlobalErrorToast) {
        createToast(`Endpoint API non trouvé (404). Vérifiez que l'endpoint existe sur le serveur.`, {
          position: 'bottom-center',
          hideProgressBar: true,
          timeout: 5000,
          transition: 'slide',
          toastBackgroundColor: '#ff4545',
        });
      } else if (error.response.status >= 500 && !suppressGlobalErrorToast) {
        createToast(`Erreur serveur d'analyse, veuillez réessayer plus tard`, {
          position: 'bottom-center',
          hideProgressBar: true,
          timeout: 3000,
          transition: 'slide',
          toastBackgroundColor: '#ff4545',
        });
      }
    } else if (error.request && !suppressGlobalErrorToast) {
      createToast(`Impossible de joindre le service d'analyse. Vérifiez votre connexion réseau.`, {
        position: 'bottom-center',
        hideProgressBar: true,
        timeout: 4000,
        transition: 'slide',
        toastBackgroundColor: '#ff4545',
      });
    }
    return Promise.reject(error);
  }
);

function eachRecursive(obj) {
  for (var k in obj) {
    if (typeof obj[k] == 'object' && obj[k] !== null) eachRecursive(obj[k]);
    // Ne pas forcer null → '' (même raison que api.js : limites / compteurs API).
  }
}

/**
 * Extrait le DID depuis les cookies ou le token JWT
 * @returns {string|null} Le DID décodé ou null si non trouvé
 */
function extractDid() {
  // 1. Essayer depuis le cookie 'did'
  const didCookie = VueCookies.get('did');
  if (didCookie) {
    // Si c'est un JWT (format: header.payload.signature)
    if (didCookie.includes('.')) {
      try {
        const payload = JSON.parse(atob(didCookie.split('.')[1]));
        return payload.did || payload.sub || payload.id;
      } catch (e) {
      }
    }
    // Si c'est du base64 simple
    try {
      return atob(didCookie);
    } catch (e) {
      // Sinon retourner tel quel
      return didCookie;
    }
  }
  
  // 2. Le token est HttpOnly (inaccessible en JS) ; le DID doit venir du store Vuex.

  return null;
}

// Cache simple pour éviter les appels API redondants
// Structure: { key: { data, timestamp } }
const apiCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Génère une clé de cache pour une requête
 */
function getCacheKey(platform, username, params = {}) {
  const sortedParams = Object.keys(params).sort().reduce((acc, key) => {
    acc[key] = params[key];
    return acc;
  }, {});
  return `${platform}_${username}_${JSON.stringify(sortedParams)}`;
}

/**
 * Vérifie si une requête est en cache et valide
 */
function getCachedData(key) {
  const cached = apiCache.get(key);
  if (!cached) return null;
  
  const now = Date.now();
  if (now - cached.timestamp > CACHE_DURATION) {
    // Cache expiré
    apiCache.delete(key);
    return null;
  }
  
  return cached.data;
}

/**
 * Met en cache les données d'une requête
 */
function setCachedData(key, data) {
  apiCache.set(key, {
    data: data,
    timestamp: Date.now()
  });
}

/**
 * Invalide le cache pour une plateforme/username
 */
function invalidateCache(platform, username) {
  const prefix = `${platform}_${username}_`;
  for (const key of apiCache.keys()) {
    if (key.startsWith(prefix)) {
      apiCache.delete(key);
    }
  }
}

async function getAnalyzerStatus() {
  return adapter.get(analyzerPath('/status'));
}

async function getXPosts(username, startDate) {
  return adapter.get(analyzerPath(`/analyzer/x/posts/${username}`), { params: startDate ? { startDate } : {} });
}

// Pour les plateformes URL-based, encoder le path; Instagram est forcé au username.
function linkedinPathSegment(platform, value) {
  if (!value || typeof value !== 'string') return value;
  const cleanPlatform = String(platform || '').trim().toLowerCase();

  if (cleanPlatform === 'instagram' || cleanPlatform === 'instagram-bd') {
    const normalizedInstagram = normalizeInstagramIdentifier(value);
    return encodeURIComponent(normalizedInstagram || value.trim());
  }

  if (cleanPlatform === 'facebook') {
    const normalizedFacebook = normalizeFacebookTargetAccount(value);
    return encodeURIComponent(normalizedFacebook || value.trim());
  }

  // Les comptes LinkedIn, YouTube, Facebook et TikTok peuvent être des URLs complètes
  // contenant des slashes; il faut les encoder pour éviter de casser le routage Express.
  const URL_PLATFORMS = ['linkedin', 'youtube', 'facebook', 'tiktok'];
  if (URL_PLATFORMS.includes(cleanPlatform)) {
    return encodeURIComponent(value.trim());
  }
  return value.trim();
}

async function getBrandAnalyzerData(platform, params = {}, useCache = true, username = null) {
  // username est maintenant obligatoire
  if (!username) {
    throw new Error(`username is required for platform ${platform}`);
  }
  const finalUsername = username;
  const pathSegment = linkedinPathSegment(platform, finalUsername);

  // Vérifier le cache si activé
  if (useCache) {
    const cacheKey = getCacheKey(platform, finalUsername, params);
    const cached = getCachedData(cacheKey);
    if (cached) {
      return { data: cached };
    }
  }

  // Log de l'appel API
  const url = analyzerPath(`/analyzer/${platform}/posts/${pathSegment}`);
  
  const startTime = Date.now();

  try {
    const response = await adapter.get(url, { params });

    // Mettre en cache si activé
    if (useCache) {
      const cacheKey = getCacheKey(platform, finalUsername, params);
      setCachedData(cacheKey, response.data);
    }
    
    return response;
  } catch (error) {
    throw error;
  }
}

async function getMemberDetail(platform, accountId, memberId, params = {}) {
  if (!accountId) {
    throw new Error(`accountId is required for platform ${platform}`);
  }
  if (!memberId) {
    throw new Error(`memberId is required for platform ${platform}`);
  }
  
  // Route spécifique pour Discord
  if (platform === 'discord') {
    return adapter.get(analyzerPath(`/analyzer/discord/users/${accountId}/${memberId}`), { params });
  }
  // LinkedIn : encoder l'URL (accountId) et le nom d'auteur (memberId) pour le path
  const accountSegment = linkedinPathSegment(platform, accountId);
  const memberSegment = memberId ? encodeURIComponent(String(memberId)) : memberId;
  return adapter.get(analyzerPath(`/analyzer/${platform}/users/${accountSegment}/${memberSegment}`), { params });
}

async function getMemberInteractions(platform, accountId, params = {}) {
  if (!accountId) {
    throw new Error(`accountId is required for platform ${platform}`);
  }
  
  // Route spécifique pour Discord
  if (platform === 'discord') {
    // Pour Discord: accountId = channelId, params.username = userId
    // Format: /analyzer/discord/interactions/{channelId}?userId={userId}&sort=...&order=...
    const discordParams = { ...params };
    // Remplacer 'username' par 'userId' pour Discord
    if (discordParams.username) {
      discordParams.userId = discordParams.username;
      delete discordParams.username;
    }
    return adapter.get(analyzerPath(`/analyzer/discord/interactions/${accountId}`), { params: discordParams });
  }
  const cleanAccountId = accountId.startsWith('@') ? accountId.slice(1) : accountId;
  const pathSegment = linkedinPathSegment(platform, cleanAccountId);
  return adapter.get(analyzerPath(`/analyzer/${platform}/interactions/${pathSegment}`), { params });
}

/**
 * Récupère les interactions pour un username spécifique
 * @param {string} platform - Plateforme ('x', 'linkedin', etc.)
 * @param {string} username - Nom d'utilisateur
 * @param {object} params - Paramètres de requête (limit, offset, startDate, endDate, etc.)
 * @returns {Promise} Response avec les interactions
 */
async function getInteractionsByUsername(platform, username, params = {}, useCache = true) {
  const cleanUsername = username.startsWith('@') ? username.slice(1) : username;
  const pathSegment = linkedinPathSegment(platform, cleanUsername);

  // Vérifier le cache si activé
  if (useCache) {
    const cacheKey = getCacheKey(platform, cleanUsername, params);
    const cached = getCachedData(cacheKey);
    if (cached) {
      return { data: cached };
    }
  }

  try {
    const response = await adapter.get(analyzerPath(`/analyzer/${platform}/interactions/${pathSegment}`), { params });
    
    // Mettre en cache si activé
    if (useCache) {
      const cacheKey = getCacheKey(platform, cleanUsername, params);
      setCachedData(cacheKey, response.data);
    }
    
    return response;
  } catch (error) {
    throw error;
  }
}

async function getPostsList(platform, username, params = {}) {
  if (!username) {
    throw new Error(`username is required for platform ${platform}`);
  }
  const cleanUsername = username.startsWith('@') ? username.slice(1) : username;
  const pathSegment = linkedinPathSegment(platform, cleanUsername);
  return adapter.get(analyzerPath(`/analyzer/${platform}/posts-list/${pathSegment}`), { params });
}

async function getPostDetail(platform, postId, username, params = {}) {
  if (!username) {
    throw new Error(`username is required for platform ${platform}`);
  }
  const cleanUsername = username.startsWith('@') ? username.slice(1) : username;
  const pathSegment = linkedinPathSegment(platform, cleanUsername);
  return adapter.get(analyzerPath(`/analyzer/${platform}/posts/${pathSegment}/${postId}`), { params });
}

/**
 * Récupère les analytics de mentions pour une marque
 * @param {string} brandId - Identifiant de la marque
 * @param {string} startDate - Date de début (ISO string)
 * @param {string} endDate - Date de fin (ISO string)
 */
async function getMentionsAnalytics(brandName, startDate, endDate, isFirstCall = true) {
  return adapter.get(analyzerPath(`/analyzer/mentions/${encodeURIComponent(brandName)}`), {
    params: {
      startDate,
      endDate,
      isFirstCall
    },
  });
}

/**
 * Récupère la liste complète des mentions avec pagination
 * @param {string} issuerDid - DID de l'émetteur
 * @param {string} brandName - Nom de la marque
 * @param {object} params - Paramètres de pagination (page, limit, startDate, endDate)
 */
async function getMentionsList(issuerDid, brandName, params = {}) {
  return adapter.get(analyzerPath('/analyzer/mentions-list'), {
    params: {
      issuerDid,
      brandName,
      page: params.page || 1,
      limit: params.limit || 20,
      startDate: params.startDate,
      endDate: params.endDate,
      sentiment: params.sentiment,
      category: params.category,
      reviewOnly: params.reviewOnly,
      search: params.search,
      period: params.period,
      sortBy: params.sortBy,
      sortOrder: params.sortOrder
    },
  });
}

/**
 * Génère un deeplink Telegram pour ajouter le bot à un groupe
 * @returns {Promise} Response avec { deeplink, code, expiresAt }
 */
async function getTelegramDeeplink() {
  return adapter.get(analyzerPath('/analyzer/telegram/generate-deeplink'));
}

/**
 * Lie automatiquement le deeplink Telegram au dernier chat détecté par webhook.
 * Utilisé par l'étape "Tester la connexion" pour éviter /start manuel.
 * @param {string} code - Code deeplink renvoyé par getTelegramDeeplink
 * @param {string|null} chatId - Optionnel, pour cibler un chat précis
 */
async function linkTelegramDeeplink(code, chatId = null) {
  const payload = { code };
  if (chatId) payload.chatId = chatId;
  return adapter.post(analyzerPath('/analyzer/telegram/link-deeplink'), payload, {
    suppressGlobalErrorToast: true,
  });
}

/**
 * Vérifie le statut d'un bot Telegram (check en base - bot déjà lié)
 * @param {string} telegramLink - Lien du canal Telegram
 */
async function checkTelegramBotStatus(telegramLink) {
  return adapter.get(analyzerPath('/analyzer/telegram/check-bot-status'), {
    params: {
      telegramLink,
    },
  });
}

/**
 * Vérifie si le bot est admin/membre du canal (API Telegram live - getChatMember)
 * À utiliser pour l'étape "Vérifier" avant bind-channel. Retourne isMember, chatId, title, username.
 * @param {string} telegramLink - Lien du canal ou username ou peer ID
 */
async function checkTelegramBotAdmin(telegramLink) {
  return adapter.get(analyzerPath('/analyzer/telegram/check-bot-admin'), {
    params: {
      telegramLink,
    },
  });
}

/**
 * Lie un canal Telegram à l'issuer et lance l'analyse. Retourne batchId pour le polling.
 * @param {string} channelId - ID du chat Telegram (retourné par check-bot-admin)
 */
async function bindTelegramChannel(channelId) {
  return adapter.post(analyzerPath('/analyzer/telegram/bind-channel'), { channelId });
}

/**
 * Debug: réinitialise la liaison Telegram pour l'issuer courant.
 * @param {string|null} chatId - Optionnel, pour cibler un chat précis
 */
async function resetTelegramBindingDebug(chatId = null) {
  const payload = {};
  if (chatId) payload.chatId = chatId;
  return adapter.post(analyzerPath('/analyzer/telegram/debug/reset-binding'), payload);
}

/**
 * Récupère les chats Telegram déjà détectés/analysés pour l'issuer courant
 */
async function getAnalyzedTelegramChats() {
  return adapter.get(analyzerPath('/analyzer/telegram/chats'));
}

/**
 * Génère un deeplink Discord OAuth2 (state one-time, TTL)
 * @returns {Promise} Response avec { deeplink, code, expiresAt }
 */
async function getDiscordDeeplink() {
  return adapter.get(analyzerPath('/analyzer/discord/generate-deeplink'));
}

/**
 * Lie un deeplink Discord au serveur validé par callback OAuth.
 * Utilisé par l'étape "Tester la connexion" du wizard.
 * @param {string} code - Code deeplink renvoyé par getDiscordDeeplink
 */
async function linkDiscordDeeplink(code) {
  return adapter.post(
    analyzerPath('/analyzer/discord/link-deeplink'),
    { code },
    { suppressGlobalErrorToast: true }
  );
}

/**
 * Vérifie le statut d'un bot Discord
 * @param {string} guildIdentifier - Identifiant du serveur Discord (guildId ou invite link)
 */
async function checkDiscordBotStatus(guildIdentifier) {
  return adapter.get(analyzerPath('/analyzer/discord/check-bot-status'), {
    params: {
      guildIdentifier,
    },
  });
}

/**
 * Récupère les channels d'un serveur Discord
 * @param {string} guildId - ID du serveur Discord
 */
async function getDiscordGuildChannels(guildId) {
  const url = analyzerPath('/analyzer/discord/guild/channels');
  
  const response = await adapter.get(url, {
    params: { guildId },
    suppressGlobalErrorToast: true,
  });
  
  return response;
}

/**
 * Vérifie si un compte Twitter/X existe
 * @param {string} username - Nom d'utilisateur Twitter (sans @)
 * @returns {Promise} Response avec { success: true, data: true/false }
 */
async function checkTwitterAccountExists(username) {
  // Retirer le @ si présent
  const cleanUsername = username.startsWith('@') ? username.slice(1) : username;
    
  try {
    const response = await adapter.get(analyzerPath('/analyzer/x/profile'), {
      params: {
        xTargetAccount: cleanUsername
      }
    });
    return response;
  } catch (error) {
    // Si 400, c'est probablement un format invalide
    if (error.response?.status === 400) {
      return {
        data: {
          success: false,
          data: false,
          message: error.response?.data?.message || 'Format de nom d\'utilisateur invalide'
        },
        status: 400
      };
    }
    
    // Si 404, le compte n'existe probablement pas
    if (error.response?.status === 404) {
      return {
        data: {
          success: true,
          data: false,
          message: 'Ce compte Twitter n\'existe pas'
        },
        status: 404
      };
    }
    
    // Si 500 ou autre erreur serveur, retourner une réponse structurée
    if (error.response?.status >= 500 || !error.response) {
      return {
        data: {
          success: false,
          data: false,
          message: error.response?.data?.message || 'Erreur serveur lors de la vérification du compte. Veuillez réessayer plus tard.'
        },
        status: error.response?.status || 500
      };
    }
    
    // Pour les autres erreurs, lancer l'exception
    throw error;
  }
}

/**
 * Vérifie si un compte LinkedIn existe
 * @param {string} accountNameOrUrl - URL complète LinkedIn (company/showcase/school) ou nom de compte
 * @returns {Promise} Response avec { success: true, data: object/true/false }
 */
async function checkLinkedinAccountExists(accountNameOrUrl) {
  const cleanAccount = accountNameOrUrl.trim();
  
  try {
    const response = await adapter.get(analyzerPath('/analyzer/linkedin/profile'), {
      params: {
        linkedinTargetAccount: cleanAccount
      }
    });
    
    return response;
  } catch (error) {
    // Si 400, c'est probablement un format invalide
    if (error.response?.status === 400) {
      return {
        data: {
          success: false,
          data: false,
          message: error.response?.data?.message || 'Format de nom de compte invalide'
        },
        status: 400
      };
    }
    
    // Si 404, le compte n'existe probablement pas
    if (error.response?.status === 404) {
      return {
        data: {
          success: true,
          data: false,
          message: 'Ce compte LinkedIn n\'existe pas'
        },
        status: 404
      };
    }
    
    // Si 500 ou autre erreur serveur, retourner une réponse structurée
    if (error.response?.status >= 500 || !error.response) {
      return {
        data: {
          success: false,
          data: false,
          message: error.response?.data?.message || 'Erreur serveur lors de la vérification du compte. Veuillez réessayer plus tard.'
        },
        status: error.response?.status || 500
      };
    }
    
    // Pour les autres erreurs, lancer l'exception
    throw error;
  }
}

/**
 * Lance l'analyse Twitter pour un compte
 * @param {string} username - Nom d'utilisateur Twitter (sans @)
 * @returns {Promise} Response avec le résultat du lancement
 */
async function launchTwitterAnalysis(username) {
  // Retirer le @ si présent
  const cleanUsername = username.startsWith('@') ? username.slice(1) : username;
  
  try {
    const response = await adapter.post(analyzerPath('/analyzer/scrape'), { xTargetAccount: cleanUsername });
    return response;
  } catch (error) {
    throw error;
  }
}

/**
 * Lance l'analyse LinkedIn pour un compte
 * @param {string} accountNameOrUrl - URL complète LinkedIn (company/showcase/school) pour le scraping, ou nom de compte
 * @returns {Promise} Response avec le résultat du lancement
 */
async function launchLinkedinAnalysis(accountNameOrUrl) {
  const cleanAccount = accountNameOrUrl.trim();
  
  try {
    // Construire l'URL complète
    const baseUrl = adapter.defaults?.baseURL || '';
    const endpoint = '/analyzer/scrape';
    const fullUrl = `${baseUrl}${endpoint}`;
    
    // Payload
    const payload = { linkedinTargetAccount: cleanAccount };
    
    const response = await adapter.post(analyzerPath('/analyzer/scrape'), payload);
    
    return response;
  } catch (error) {
    throw error;
  }
}

/**
 * Lance l'analyse Web/Mention pour une marque (Scraping)
 * @param {Object} data - Données pour l'analyse
 * @returns {Promise} Response avec le résultat du lancement
 */
async function launchWebAnalysis(data) {
  try {
    const response = await adapter.post(analyzerPath('/analyzer/mentions/scrape'), data);
    return response;
  } catch (error) {
    throw error;
  }
}

/**
 * Réinitialise l'analyse Mentions (archive les données + supprime l'association webTargetAccount)
 */
async function resetMentionsAnalysis() {
  return adapter.post(analyzerPath('/analyzer/account/mentions/reset'));
}

/**
 * Réinitialise l'analyse LinkedIn (dev / opérations : archive les données + supprime l'association linkedinTargetAccount)
 */
async function resetLinkedinAnalysis() {
  return adapter.post(analyzerPath('/analyzer/account/linkedin/reset'));
}

/**
 * Réinitialise l'analyse X (archive les données + supprime l'association xTargetAccount)
 */
async function resetXAnalysis() {
  return adapter.post(analyzerPath('/analyzer/account/x/reset'));
}

/**
 * Lance l'analyse Discord pour un channel
 * @param {string} channelId - ID du channel Discord
 * @param {string} timeKey - Période d'analyse (ex: "last_30_days")
 * @returns {Promise} Response avec le résultat du lancement
 */
async function launchDiscordAnalysis(channelId, timeKey = 'last_90_days') {
  const cleanChannelId = channelId.trim();
  
  try {
    const response = await adapter.post(analyzerPath('/analyzer/discord'), {
      channelId: cleanChannelId,
      timeKey
    });
    
    return response;
  } catch (error) {
    throw error;
  }
}

/**
 * Récupère les données d'analyse Discord
 * @param {Object} params - Paramètres de requête (startDate, endDate, etc.)
 * @returns {Promise} Response avec les données d'analyse Discord
 */
async function getDiscordAnalysis(params = {}) {
  try {
    const response = await adapter.get(analyzerPath('/analyzer/discord/analysis'), { params });
    
    return response;
  } catch (error) {
    throw error;
  }
}

/**
 * Récupère le statut d'une analyse Discord
 * @param {string} batchId - ID de l'analyse (batchId)
 * @returns {Promise} Response avec le statut de l'analyse
 */
async function getDiscordAnalysisStatus(batchId) {
  try {
    const response = await adapter.get(analyzerPath(`/analyzer/discord/analysis/status/${batchId}`));
    
    return response;
  } catch (error) {
    throw error;
  }
}

/**
 * Récupère les données d'analyse d'un channel Discord
 * @param {string} channelId - ID du channel Discord
 * @param {Object} params - Paramètres (startDate, endDate)
 * @returns {Promise} Response avec les données d'analyse du channel
 */
async function getDiscordMessages(channelId, params = {}) {
  try {
    const url = analyzerPath(`/analyzer/discord/messages/${channelId}`);
    
    const response = await adapter.get(url, { params });
    
    return response;
  } catch (error) {
    throw error;
  }
}

/**
 * Récupère les détails d'un message Discord spécifique
 * @param {string} channelId - ID du channel Discord
 * @param {string} messageId - ID du message
 * @param {Object} params - Paramètres (page, limit)
 * @returns {Promise} Response avec les détails du message et ses réponses
 */
async function getDiscordMessageDetails(channelId, messageId, params = {}) {
  try {
    const response = await adapter.get(analyzerPath(`/analyzer/discord/messages/${channelId}/${messageId}`), { params });
    
    return response;
  } catch (error) {
    throw error;
  }
}

/**
 * Récupère les interactions d'un utilisateur sur un channel Discord
 * @param {string} channelId - ID du channel Discord
 * @param {Object} params - Paramètres (userId, sort, order, page, limit)
 * @returns {Promise} Response avec les interactions de l'utilisateur
 */
async function getDiscordUserInteractions(channelId, params = {}) {
  try {
    const response = await adapter.get(analyzerPath(`/analyzer/discord/interactions/${channelId}`), { params });
    
    return response;
  } catch (error) {
    throw error;
  }
}

/**
 * Récupère la matrice globale d'analyse d'un channel Discord
 * @param {string} channelId - ID du channel Discord
 * @returns {Promise} Response avec les métriques globales du channel
 */
async function getDiscordChannelMetrics(channelId) {
  try {
    const response = await adapter.get(analyzerPath(`/analyzer/discord/${channelId}`));
    
    return response;
  } catch (error) {
    throw error;
  }
}

/**
 * Récupère la liste de tous les channels Discord analysés
 * @returns {Promise} Response avec la liste des channels
 */
async function getDiscordChannels() {
  const url = analyzerPath('/analyzer/discord/channels');
  const response = await adapter.get(url, { suppressGlobalErrorToast: true });
  return response;
}

/**
 * Crée une nouvelle analyse Discord pour un channel
 * @param {Object} data - { channelId, guildId }
 * @returns {Promise} Response avec le batchId
 */
async function createDiscordAnalysis(data) {
  try {
    const url = analyzerPath('/analyzer/discord');
    const response = await adapter.post(url, data);
    return response;
  } catch (error) {
    throw error;
  }
}

/**
 * Récupère le statut d'un batch d'analyse
 * @param {string} batchId - ID du batch
 * @returns {Promise} Response avec le statut du batch et des jobs
 */
/**
 * URLs à tester : originale puis variante avec ou sans préfixe www (ex. pizzahut.fr ↔ www.pizzahut.fr).
 * Évite les doubles si identiques ; pas de www pour localhost / IP / hostname sans point.
 */
function websiteUrlVariantsForAccessibilityProbe(rawUrl) {
  const trimmed = String(rawUrl || '').trim();
  if (!trimmed) return [];
  try {
    const u = new URL(/^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`);
    const host = u.hostname.toLowerCase();
    const variants = [];
    const push = (hostname) => {
      const nu = new URL(u.href);
      nu.hostname = hostname;
      const href = nu.toString();
      if (!variants.includes(href)) variants.push(href);
    };
    push(host);
    if (host.startsWith('www.')) {
      const bare = host.slice(4);
      if (bare) push(bare);
    } else if (host.includes('.') && !/^\d+\.\d+\.\d+\.\d+$/.test(host) && host !== 'localhost') {
      push(`www.${host}`);
    }
    return variants;
  } catch {
    return [trimmed];
  }
}

/**
 * Vérifie si un site web est accessible avant le lancement de l'audit Brand Presence.
 * Essaie successivement l’URL donnée puis la variante www / sans-www si pertinent.
 * @param {string} url - URL du site à tester
 * @returns {Promise<{ accessible: boolean, status?: number, error?: string, checkedUrl?: string }>}
 */
async function checkBrandPresenceWebsite(url) {
  const variants = websiteUrlVariantsForAccessibilityProbe(url);
  let last = { accessible: false, error: 'unknown' };

  for (const probeUrl of variants) {
    try {
      const response = await adapter.post(analyzerPath('/analyzer/brand-presence/check-website'), {
        url: probeUrl,
      });
      const data = response.data || response;
      if (data && data.accessible) {
        return {
          ...data,
          checkedUrl: probeUrl,
        };
      }
      last = data && typeof data === 'object' ? { ...data } : last;
    } catch (error) {
      last = { accessible: false, error: error.message || 'unknown' };
    }
  }

  return last;
}

/**
 * Lance l'audit Brand Presence (présence de la marque sur les IA)
 * @param {Object} data - { brand: { name, description? } }
 * @returns {Promise} Response avec callbackId, batchId, status, etc.
 */
async function launchBrandPresenceAudit(data) {
  try {
    const response = await adapter.post(analyzerPath('/analyzer/brand-presence/audit'), data);
    return response;
  } catch (error) {
    throw error;
  }
}

/**
 * Récupère le statut d'une analyse Brand Presence
 * @param {string} callbackId - ID retourné par launchBrandPresenceAudit
 * @returns {Promise} Response avec status (PROGRESS | COMPLETE | ERROR), progress, etc.
 */
async function getBrandPresenceAnalysisStatus(callbackId) {
  try {
    const response = await adapter.get(analyzerPath(`/analyzer/brand-presence/analysis/status/${callbackId}`));
    return response;
  } catch (error) {
    throw error;
  }
}

/**
 * Récupère le résultat d'une analyse Brand Presence (quand status === COMPLETE)
 * @param {string} callbackId - ID de l'analyse
 * @param {string} lang - Langue (fr | en), défaut fr
 * @returns {Promise} Response avec report_markdown, assessment, brand_name, etc.
 */
async function getBrandPresenceAnalysisResult(callbackId, lang = 'fr') {
  try {
    const response = await adapter.get(analyzerPath(`/analyzer/brand-presence/analysis/result/${callbackId}`), {
      params: { lang: lang === 'en' ? 'en' : 'fr' },
    });
    return response;
  } catch (error) {
    throw error;
  }
}

/**
 * Métadonnées audit brand-presence (dernier refresh, cadence) — mémoire serveur.
 */
async function getBrandPresenceMeta() {
  try {
    const response = await adapter.get(analyzerPath('/analyzer/brand-presence/meta'));
    return response;
  } catch (error) {
    throw error;
  }
}

async function getBatchStatus(batchId) {
  try {
    const url = analyzerPath(`/analyzer/batch/status/${batchId}`);

    const response = await adapter.get(url);
    
    return response;
  } catch (error) {
    throw error;
  }
}

/**
 * Lance l'analyse combinée (Web, X, LinkedIn) pour l'onboarding.
 * Comme le dashboard (mentions/scrape) : nom de marque brut + website/description optionnels ;
 * pas d'extraction hostname côté client — le backend normalise comme pour POST …/mentions/scrape.
 *
 * @param {string} webTargetAccount - Nom de marque (identique au flux dashboard)
 * @param {string} xTargetAccount - Nom d'utilisateur Twitter/X
 * @param {string} linkedinTargetAccount - Nom du compte LinkedIn
 * @param {string} website - URL du site (optionnel)
 * @param {string} description - Description marque (optionnel)
 * @param {string} language - Langue principale de l'analyse web
 * @param {object} options - Options d'orchestration du batch
 * @returns {Promise} Response avec le résultat du lancement
 */
async function launchCombinedAnalysis(webTargetAccount, xTargetAccount, linkedinTargetAccount, website, description, language, options = {}) {
  try {
    // Nettoyer les valeurs
    const cleanWeb = webTargetAccount?.trim() || '';
    const cleanX = xTargetAccount?.trim() || '';
    const cleanLinkedin = linkedinTargetAccount?.trim() || '';
    const cleanWebsite = website?.trim() || '';
    const cleanDescription = description?.trim() || '';
    
    // Retirer le @ si présent pour Twitter
    const cleanXUsername = cleanX.startsWith('@') ? cleanX.slice(1) : cleanX;

    // Au moins une cible réseau (aligné backend : jobs vides → 400)
    if (!cleanWeb && !cleanXUsername && !cleanLinkedin) {
      throw new Error('Vous devez fournir au moins une cible d’analyse (marque web, X ou LinkedIn)');
    }
    
    const body = {};
    if (cleanWeb) body.webTargetAccount = cleanWeb;
    if (cleanXUsername) body.xTargetAccount = cleanXUsername;
    if (cleanLinkedin) body.linkedinTargetAccount = cleanLinkedin;
    if (cleanWebsite) body.website = cleanWebsite;
    if (cleanDescription) body.description = cleanDescription;
    if (language) body.language = String(language).trim();
    if (options.launchAiPresence === true) body.launchAiPresence = true;
    if (options.aiIdempotencyKey) {
      body.aiIdempotencyKey = String(options.aiIdempotencyKey).trim();
    }
    
    const response = await adapter.post(analyzerPath('/analyzer/batch/analysis'), body);
    
    return response;
  } catch (error) {
    throw error;
  }
}

/**
 * Notifie le service analyzer du changement de DID après un switch.
 * Appel au backend (proxy) avec l'ancien token pour que l'analyzer reçoive oldDid via x-issuer-did.
 * @param {string} newDid - Le nouveau DID créé
 * @param {string} oldToken - L'ancien token (avant le switch), pour authentifier en tant qu'ancien utilisateur
 */
async function notifyDidSwitch(newDid, oldToken) {
  try {
    const url = `${APIBaseURL}${analyzerPath('/analyzer/account/update')}`;
    const response = await Axios.post(url, { newDid }, {
      headers: {
        Authorization: `Bearer ${oldToken}`,
        'Content-Type': 'application/json',
      },
      timeout: 10000,
    });
    return response;
  } catch (error) {
    throw error;
  }
}

/**
 * Corrige le score d'une interaction (post/commentaire) lorsque l'IA a fait une erreur
 * @param {string} interactionId - ID de l'interaction à corriger
 * @param {object} payload - { newSentiment, newUserScore, startDate, endDate }
 * @returns {Promise} Response
 */
async function correctInteractionScore(interactionId, payload) {
  return adapter.patch(analyzerPath(`/analyzer/interactions/${interactionId}/correct`), payload);
}

/**
 * Récupère le statut de configuration des analyses pour un issuer
 * @param {string} issuerDid - DID de l'issuer
 * @returns {Promise} Response avec la configuration (webTargetAccount, xTargetAccount, linkedinTargetAccount, etc.)
 */
async function getAnalyzerAccountStatus(issuerDid) {
  const url = analyzerPath('/analyzer/account/status');
  const params = { issuerDid };

  return adapter.get(url, { params });
}

// ============================================================================
// BRIGHT DATA SOCIAL NETWORKS API FUNCTIONS
// ============================================================================

async function getYoutubeProfile(channelUrl) {
  try {
    const response = await adapter.get(analyzerPath('/analyzer/youtube/profile'), {
      params: { youtubeTargetAccount: channelUrl },
      timeout: 120000,
    });
    return response;
  } catch (error) {
    if (error.response?.status >= 400) return { data: { success: false, data: null, status: 'PROFILE_NOT_FOUND' } };
    throw error;
  }
}

async function launchYoutubeAnalysis(channelUrl) {
  return adapter.post(analyzerPath('/analyzer/scrape'), { youtubeTargetAccount: channelUrl.trim() });
}

async function getFacebookProfile(pageUrl) {
  const normalizedTarget =
    normalizeFacebookTargetAccount(pageUrl) || String(pageUrl || '').trim();
  try {
    const response = await adapter.get(analyzerPath('/analyzer/facebook/profile'), {
      params: { facebookTargetAccount: normalizedTarget },
      timeout: 120000,
    });
    return response;
  } catch (error) {
    if (error.response?.status >= 400) return { data: { success: false, data: null, status: 'PROFILE_NOT_FOUND' } };
    throw error;
  }
}

async function launchFacebookAnalysis(pageUrl) {
  const normalizedTarget =
    normalizeFacebookTargetAccount(pageUrl) || String(pageUrl || '').trim();
  return adapter.post(analyzerPath('/analyzer/scrape'), {
    facebookTargetAccount: normalizedTarget,
  });
}

async function getInstagramProfile(username) {
  const normalizedUsername = normalizeInstagramIdentifier(username);
  try {
    const response = await adapter.get(analyzerPath('/analyzer/instagram-bd/profile'), {
      params: { instagramTargetAccount: normalizedUsername || String(username || '').trim() },
      timeout: 120000,
    });
    return response;
  } catch (error) {
    const status = error.response?.status;
    if (status === 404) return { data: { success: false, data: null, status: 'PROFILE_NOT_FOUND' } };
    if (status === 403) {
      return {
        data: {
          success: false,
          data: null,
          status: 'FORBIDDEN',
          message:
            error.response?.data?.message ||
            "Cette source d'analyse n'est pas incluse dans votre offre actuelle.",
        },
      };
    }
    throw error;
  }
}

async function launchInstagramAnalysis(username) {
  const normalizedUsername = normalizeInstagramIdentifier(username);
  return adapter.post(analyzerPath('/analyzer/scrape'), {
    instagramTargetAccount: normalizedUsername || String(username || '').trim(),
  });
}

async function launchInstagramBackfill(username, options = {}) {
  const normalizedUsername = normalizeInstagramIdentifier(username);
  return launchSocialBackfill('instagram', normalizedUsername || String(username || '').trim(), options);
}

async function launchSocialBackfill(platform, targetAccount, options = {}) {
  const normalizedPlatform = String(platform || '').trim().toLowerCase();
  const fieldByPlatform = {
    instagram: 'instagramTargetAccount',
    youtube: 'youtubeTargetAccount',
    tiktok: 'tiktokTargetAccount',
  };
  const bodyField = fieldByPlatform[normalizedPlatform];
  if (!bodyField) {
    throw new Error(`Unsupported backfill platform: ${platform}`);
  }
  const normalizedTarget =
    normalizedPlatform === 'instagram'
      ? normalizeInstagramIdentifier(targetAccount) || String(targetAccount || '').trim()
      : String(targetAccount || '').trim();
  return adapter.post(analyzerPath(`/analyzer/${normalizedPlatform}/backfill`), {
    [bodyField]: normalizedTarget,
    periodDays: options.periodDays || 90,
    ...(options.referenceDate ? { referenceDate: options.referenceDate } : {}),
    ...(options.sinceDate ? { sinceDate: options.sinceDate } : {}),
    ...(options.untilDate ? { untilDate: options.untilDate } : {}),
    ...(options.postsLimit ? { postsLimit: options.postsLimit } : {}),
  });
}

async function launchYoutubeBackfill(channelUrl, options = {}) {
  return launchSocialBackfill('youtube', channelUrl, options);
}

async function launchTiktokBackfill(profileUrl, options = {}) {
  return launchSocialBackfill('tiktok', profileUrl, options);
}

async function getTiktokProfile(profileUrl) {
  try {
    const response = await adapter.get(analyzerPath('/analyzer/tiktok/profile'), {
      params: { tiktokTargetAccount: profileUrl },
      timeout: 300000,
    });
    return response;
  } catch (error) {
    if (error.response?.status >= 400) return { data: { success: false, data: null, status: 'PROFILE_NOT_FOUND' } };
    throw error;
  }
}

async function launchTiktokAnalysis(profileUrl) {
  return adapter.post(analyzerPath('/analyzer/scrape'), { tiktokTargetAccount: profileUrl.trim() });
}

async function disconnectTelegram() {
  return adapter.post(analyzerPath('/analyzer/telegram/disconnect'));
}

/**
 * Remet à zéro toutes les données d'une plateforme Bright Data (youtube, facebook, instagram, tiktok).
 * Supprime : AnalysisLog, Interactors, Interactions, BrightDataSnapshotCache, ProfileCache,
 * et réinitialise le compte cible dans Account.
 */
async function resetBrightDataPlatform(platform) {
  return adapter.post(analyzerPath(`/analyzer/brightdata/${platform}/reset`));
}

/** Détecte les hôtes TikTok CDN (utilisé pour le proxy backend avec bons headers Referer/Origin). */
function isTiktokCdnUrl(rawUrl) {
  if (!rawUrl || typeof rawUrl !== 'string') return false;
  try {
    const host = new URL(rawUrl.trim()).hostname.toLowerCase();
    return host === 'tiktokcdn.com' || host.endsWith('.tiktokcdn.com');
  } catch (_) {
    return false;
  }
}

/** Valeur pour l’attribut referrerpolicy des images dont le src est une URL tiktokcdn.com (chargement direct). */
function imageReferrerPolicyForUrl(src) {
  return undefined; // proxy backend gère le Referer TikTok
}

/**
 * Toujours passer par le proxy backend (utile en repli si le navigateur ne résout pas *.tiktokcdn.com).
 * Ne jamais re-wrapper une URL déjà servie par cached-image (sinon 400 côté analyzer).
 */
function isAnalyzerCachedImageUrl(rawUrl) {
  const value = String(rawUrl || '').trim();
  if (!value) return false;
  return value.includes('/analyzer/cached-image/');
}

function proxyImageUrlThroughBackend(rawUrl) {
  if (!rawUrl) return '';
  const trimmed = String(rawUrl).trim();
  if (trimmed.startsWith('data:')) return trimmed;
  // cached-image is already served by the panel/analyzer — never nest in proxy-image.
  if (isAnalyzerCachedImageUrl(trimmed)) return trimmed;
  try {
    const base = (APIBaseURL || '').replace(/\/+$/, '');
    const prefix = base.endsWith('/api/v1') ? '' : '/api/v1';
    return `${base}${prefix}/analyzer/proxy-image?imageUrl=${encodeURIComponent(trimmed)}`;
  } catch (_) {
    return trimmed;
  }
}

/**
 * Construit l'URL proxifiée pour afficher une image distante (YouTube, Instagram, etc.)
 * via le backend, évitant les blocages CORS/CSP dans le navigateur.
 * Les URLs *.tiktokcdn.com sont d’abord servies en direct (+ no-referrer) ; en cas d’échec (@error),
 * 
 */
function proxyImageUrl(rawUrl) {
  if (!rawUrl) return '';
  const trimmed = String(rawUrl).trim();
  if (isAnalyzerCachedImageUrl(trimmed)) return trimmed;
  return proxyImageUrlThroughBackend(trimmed);
}

export default {
  install(app) {
    let analyzerApi = {
      analyzerAdapter: adapter,
      getAnalyzerStatus,
      getXPosts,
      getBrandAnalyzerData,
      getMemberDetail,
      getMemberInteractions,
      getInteractionsByUsername,
      getPostsList,
      getPostDetail,
      getMentionsAnalytics,
      getMentionsList,
      checkTelegramBotStatus,
      checkTelegramBotAdmin,
      getTelegramDeeplink,
      linkTelegramDeeplink,
      bindTelegramChannel,
      resetTelegramBindingDebug,
      getAnalyzedTelegramChats,
      getDiscordDeeplink,
      linkDiscordDeeplink,
      checkDiscordBotStatus,
      getDiscordGuildChannels,
      getAnalyzerAccountStatus,
      checkTwitterAccountExists,
      checkLinkedinAccountExists,
      launchTwitterAnalysis,
      launchLinkedinAnalysis,
      launchWebAnalysis,
      resetMentionsAnalysis,
      resetLinkedinAnalysis,
      resetXAnalysis,
      launchDiscordAnalysis,
      getDiscordAnalysis,
      getDiscordAnalysisStatus,
      getDiscordMessages,
      getDiscordMessageDetails,
      getDiscordUserInteractions,
      getDiscordChannelMetrics,
      getDiscordChannels,
      createDiscordAnalysis,
      launchCombinedAnalysis,
      getBatchStatus,
      checkBrandPresenceWebsite,
      launchBrandPresenceAudit,
      getBrandPresenceAnalysisStatus,
      getBrandPresenceAnalysisResult,
      getBrandPresenceMeta,
      notifyDidSwitch,
      invalidateCache,
      correctInteractionScore,
      getYoutubeProfile,
      launchYoutubeAnalysis,
      getFacebookProfile,
      launchFacebookAnalysis,
      getInstagramProfile,
      launchInstagramAnalysis,
      launchInstagramBackfill,
      launchSocialBackfill,
      launchYoutubeBackfill,
      launchTiktokBackfill,
      getTiktokProfile,
      launchTiktokAnalysis,
      disconnectTelegram,
      proxyImageUrl,
      proxyImageUrlThroughBackend,
      isTiktokCdnUrl,
      imageReferrerPolicyForUrl,
      resetBrightDataPlatform,
    };
    
    // Créer et fournir le StateManager
    const stateManager = new AnalyzerStateManager(analyzerApi);
    app.provide('analyzerStateManager', stateManager);
    
    app.provide('analyzerApi', analyzerApi);
  },
};

export {
  normalizeInstagramIdentifier,
  adapter as analyzerAdapter, 
  getAnalyzerStatus, 
  getXPosts, 
  getBrandAnalyzerData, 
  getMemberDetail, 
  getMemberInteractions,
  correctInteractionScore,
  getInteractionsByUsername,
  getPostsList, 
  getPostDetail,
  getMentionsAnalytics,
  getMentionsList,
  checkTelegramBotStatus,
  checkTelegramBotAdmin,
  getTelegramDeeplink,
  linkTelegramDeeplink,
  bindTelegramChannel,
  resetTelegramBindingDebug,
  getAnalyzedTelegramChats,
  getDiscordDeeplink,
  linkDiscordDeeplink,
  checkDiscordBotStatus,
  getDiscordGuildChannels,
  createDiscordAnalysis,
  getAnalyzerAccountStatus,
  checkTwitterAccountExists,
  checkLinkedinAccountExists,
  launchTwitterAnalysis,
  launchLinkedinAnalysis,
  launchWebAnalysis,
  resetMentionsAnalysis,
  resetLinkedinAnalysis,
  resetXAnalysis,
  launchDiscordAnalysis,
  getDiscordAnalysis,
  getDiscordAnalysisStatus,
  getDiscordMessages,
  getDiscordMessageDetails,
  getDiscordUserInteractions,
  getDiscordChannelMetrics,
  getDiscordChannels,
  launchCombinedAnalysis,
  getBatchStatus,
  checkBrandPresenceWebsite,
  launchBrandPresenceAudit,
  getBrandPresenceAnalysisStatus,
  getBrandPresenceAnalysisResult,
  getBrandPresenceMeta,
  notifyDidSwitch,
  invalidateCache,
  getYoutubeProfile,
  launchYoutubeAnalysis,
  getFacebookProfile,
  launchFacebookAnalysis,
  getInstagramProfile,
  launchInstagramAnalysis,
  launchInstagramBackfill,
  launchSocialBackfill,
  launchYoutubeBackfill,
  launchTiktokBackfill,
  getTiktokProfile,
  launchTiktokAnalysis,
  disconnectTelegram,
  proxyImageUrl,
  proxyImageUrlThroughBackend,
  isTiktokCdnUrl,
  imageReferrerPolicyForUrl,
  resetBrightDataPlatform,
};
